import { useEffect, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { IndexLink } from "@/components/ui/ContractLink";
import { brokerApi } from "@/lib/brokerApi";
import { ApiError } from "@/lib/api";
import { atmStrike, dayRuleLabel, instrumentForDay, type InstrumentInfo } from "@/lib/instrument";
import { formatStaleHint, readCachedSpot, writeCachedSpot } from "@/lib/spotCache";

const BROKER_STATUS_KEY = ["broker", "status"] as const;
const LTP_POLL_MS = 5_000;
const CLOCK_MS = 30_000;

/** 3-card live trading-context row for the strategy detail page.
 *  - Instrument: derived from day rule (Mon/Tue=NIFTY, Wed/Thu=SENSEX, Fri=NIFTY)
 *  - Spot:       real, polled every 5s via existing brokerApi.ltp
 *                Falls back to localStorage cache when broker is disconnected.
 *  - ATM Strike: computed from spot (live OR cached)
 *
 * Was 5-card until 2026-07-01 (P34) — the CE/PE Position placeholders
 * were replaced with a proper OpenPositionCard elsewhere on the page.
 */
export function MarketStatusRow(): ReactElement {
  const now = useTickingClock();
  const instrument = instrumentForDay(now);
  const broker = useQuery({
    queryKey: BROKER_STATUS_KEY,
    queryFn: () => brokerApi.status(),
    refetchInterval: 30_000,
  });
  const connected = broker.data?.connected ?? false;
  const spotQuery = useSpotLtp(instrument, connected);

  const spot = useResolvedSpot(instrument.symbol, spotQuery.data?.last_price ?? null);
  const atm = spot !== null ? atmStrike(spot.value, instrument.strikeStep) : null;
  const spotError = spotQuery.error instanceof ApiError ? spotQuery.error : null;

  return (
    <section className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-3">
      <InstrumentCard instrument={instrument} now={now} />
      <SpotCard spot={spot} connected={connected} error={spotError} />
      <AtmCard instrument={instrument} atm={atm} stale={spot?.stale ?? false} />
    </section>
  );
}

interface ResolvedSpot {
  value: number;
  /** True when the value came from localStorage (not the live query). */
  stale: boolean;
  /** Ms since the value was captured. 0 when live. */
  ageMs: number;
}

/** Returns the spot to display: live LTP if available, else last cached. */
function useResolvedSpot(symbol: string, live: number | null): ResolvedSpot | null {
  useEffect(() => {
    if (live !== null) writeCachedSpot(symbol, live);
  }, [symbol, live]);
  if (live !== null) return { value: live, stale: false, ageMs: 0 };
  const cached = readCachedSpot(symbol);
  if (cached === null) return null;
  return { value: cached.value, stale: true, ageMs: Date.now() - cached.ts };
}

function useTickingClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, CLOCK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);
  return now;
}

function useSpotLtp(
  instrument: InstrumentInfo,
  connected: boolean,
): ReturnType<typeof useQuery<{ symbol: string; last_price: number }, unknown>> {
  return useQuery({
    queryKey: ["ltp", instrument.symbol],
    queryFn: () => brokerApi.ltp(instrument.symbol),
    enabled: connected,
    refetchInterval: connected ? LTP_POLL_MS : false,
    retry: false,
  });
}

function StatCell({
  label,
  value,
  hint,
  valueClass,
}: Readonly<{
  label: string;
  value: ReactElement | string;
  hint?: string;
  valueClass?: string;
}>): ReactElement {
  return (
    <div className="rounded-ctl border border-line bg-surface-1 p-3">
      <div className="text-[10px] font-medium uppercase tracking-wider text-ink-secondary">
        {label}
      </div>
      <div className={`mt-1 text-lg font-medium ${valueClass ?? ""}`}>{value}</div>
      {hint !== undefined && <div className="mt-0.5 text-[11px] text-ink-muted">{hint}</div>}
    </div>
  );
}

function InstrumentCard({
  instrument,
  now,
}: Readonly<{ instrument: InstrumentInfo; now: Date }>): ReactElement {
  return (
    <StatCell
      label="Instrument"
      value={<IndexLink name={instrument.name} />}
      hint={`Auto (${dayRuleLabel(now)})`}
      valueClass="text-accent"
    />
  );
}

interface SpotCardProps {
  spot: ResolvedSpot | null;
  connected: boolean;
  error: ApiError | null;
}

function spotHint(spot: ResolvedSpot, connected: boolean): string {
  if (spot.stale) return formatStaleHint(Date.now() - spot.ageMs, Date.now());
  if (connected) return `Last ${nowHHmmss()}`;
  return "Cached";
}

function SpotCard({ spot, connected, error }: Readonly<SpotCardProps>): ReactElement {
  if (spot !== null) {
    return (
      <StatCell label="Spot" value={formatSpot(spot.value)} hint={spotHint(spot, connected)} />
    );
  }
  if (!connected) {
    return <StatCell label="Spot" value="-" hint="Broker disconnected" />;
  }
  if (error !== null) {
    return <StatCell label="Spot" value="-" hint={`Fetch failed (HTTP ${String(error.status)})`} />;
  }
  return <StatCell label="Spot" value="-" hint="Loading..." />;
}

interface AtmCardProps {
  instrument: InstrumentInfo;
  atm: number | null;
  stale: boolean;
}

function AtmCard({ instrument, atm, stale }: Readonly<AtmCardProps>): ReactElement {
  const baseHint = `Nearest ${String(instrument.strikeStep)}`;
  const hint = stale ? `${baseHint} - from cache` : baseHint;
  return (
    <StatCell
      label="ATM Strike"
      value={atm !== null ? atm.toLocaleString("en-IN") : "-"}
      hint={hint}
    />
  );
}

function formatSpot(value: number): string {
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function nowHHmmss(): string {
  const d = new Date();
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}
